// Test database connection page
import React, { useState, useEffect } from 'react';
import { supabaseService } from '../services/supabaseClient.js';

export default function TestPage() {
  const [testResults, setTestResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const runTests = async () => {
    setIsLoading(true);
    const results = [];

    try {
      // Test 1: Count entries
      results.push({ test: 'Count Entries', status: 'running' });
      setTestResults([...results]);

      const { count, error: countError } = await supabaseService.client
        .from('whitelist_entries')
        .select('*', { count: 'exact', head: true });

      if (countError) {
        results[0] = { 
          test: 'Count Entries', 
          status: 'failed', 
          error: countError.message,
          details: countError 
        };
      } else {
        results[0] = { 
          test: 'Count Entries', 
          status: 'passed', 
          result: `Found ${count || 0} entries` 
        };
      }

      // Test 2: Insert test entry
      results.push({ test: 'Insert Test Entry', status: 'running' });
      setTestResults([...results]);

      const testData = {
        name: 'Test User',
        email: `test-${Date.now()}@example.com`,
        wallet_address: '0x1234567890123456789012345678901234567890',
        ip_address: '127.0.0.1',
        user_agent: 'Test Script',
        email_sent: false,
        email_verified: false
      };

      const { data, error: insertError } = await supabaseService.client
        .from('whitelist_entries')
        .insert([testData])
        .select();

      if (insertError) {
        results[1] = { 
          test: 'Insert Test Entry', 
          status: 'failed', 
          error: insertError.message,
          details: insertError 
        };
      } else {
        results[1] = { 
          test: 'Insert Test Entry', 
          status: 'passed', 
          result: 'Entry inserted successfully',
          entryId: data[0]?.id 
        };

        // Clean up test entry
        if (data[0]?.id) {
          await supabaseService.client
            .from('whitelist_entries')
            .delete()
            .eq('id', data[0].id);
        }
      }

      setTestResults(results);
    } catch (error) {
      results.push({ 
        test: 'Unexpected Error', 
        status: 'failed', 
        error: error.message 
      });
      setTestResults(results);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Database Connection Test</h1>
        
        <div className="mb-8">
          <button
            onClick={runTests}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {isLoading ? 'Running Tests...' : 'Run Database Tests'}
          </button>
        </div>

        <div className="space-y-4">
          {testResults.map((result, index) => (
            <div 
              key={index} 
              className={`p-4 rounded-lg ${
                result.status === 'passed' ? 'bg-green-900 border border-green-700' :
                result.status === 'failed' ? 'bg-red-900 border border-red-700' :
                'bg-yellow-900 border border-yellow-700'
              }`}
            >
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">{result.test}</h3>
                <span className={`px-2 py-1 rounded text-sm ${
                  result.status === 'passed' ? 'bg-green-700' :
                  result.status === 'failed' ? 'bg-red-700' :
                  'bg-yellow-700'
                }`}>
                  {result.status.toUpperCase()}
                </span>
              </div>
              
              {result.result && (
                <p className="mt-2 text-gray-300">{result.result}</p>
              )}
              
              {result.error && (
                <div className="mt-2">
                  <p className="text-red-300">Error: {result.error}</p>
                  {result.details && (
                    <pre className="mt-2 text-xs text-gray-400 bg-black p-2 rounded overflow-auto">
                      {JSON.stringify(result.details, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}